import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import handleError from "@composables/handleError";
import { useSurveyStore } from "@stores/survey/survey";
import { fetchInterviewByToken } from "@/utils/fetchInterviewByToken";
import type { Interview, InterviewObjective } from "@/types";
import { captureEvent } from "@/lib/analytics";

// Click-to-source on AI Insights quotes: resolves a `@resp(token, message_id)` citation
// to the matching interview and drives the InterviewDetailPanel hosted on SurveyAssistant.
export const useQuoteSourceStore = defineStore("assistantQuoteSource", () => {
  const surveyStore = useSurveyStore();
  const { token: surveyToken } = storeToRefs(surveyStore);

  const isPanelOpen = ref(false);
  const selectedInterview = ref<Interview | null>(null);
  const objectives = ref<InterviewObjective[]>([]);
  const pendingMessageId = ref<number | null>(null);
  const loading = ref(false);

  const openQuoteSource = async (respondentToken: string, messageId: number | null) => {
    if (loading.value || !respondentToken) return;
    loading.value = true;
    try {
      const found = await fetchInterviewByToken(surveyToken.value, respondentToken, messageId);
      if (!found) return;

      objectives.value = found.objectives;
      pendingMessageId.value = messageId;
      selectedInterview.value = found.interview;
      isPanelOpen.value = true;

      captureEvent("ai_insight_quote_source_opened");
    } catch (err) {
      handleError(err);
    } finally {
      loading.value = false;
    }
  };

  const close = () => {
    isPanelOpen.value = false;
  };

  return { isPanelOpen, selectedInterview, objectives, pendingMessageId, loading, openQuoteSource, close };
});
